import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useWearableTimeseries } from '../../hooks/useWearables';
import {
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Skeleton,
  Select,
  TextInput,
  Alert,
} from '@mantine/core';
import { IconHeart, IconRefresh } from '@tabler/icons-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

const RESOLUTION_OPTIONS = [
  { value: 'raw', label: 'Raw' },
  { value: '1min', label: '1 min' },
  { value: '5min', label: '5 min' },
  { value: '15min', label: '15 min' },
  { value: '1hour', label: '1 hour' },
];

function getRangeTimes(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days === 'all' ? 12000 : Number(days)));
  return { start_time: start.toISOString(), end_time: end.toISOString() };
}

function buildCustomRange(startDate, endDate) {
  const end = endDate ? new Date(`${endDate}T23:59:59Z`) : new Date();
  const start = new Date(`${startDate}T00:00:00Z`);
  return {
    start_time: start.toISOString(),
    end_time: end.toISOString(),
  };
}

function HeartRate() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [rangeDays, setRangeDays] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [resolution, setResolution] = useState('raw');
  const [allData, setAllData] = useState([]);

  const { loading, error, refresh } = useWearableTimeseries({
    autoFetch: false,
    types: ['heart_rate', 'resting_heart_rate'],
    limit: 100,
    resolution,
  });

  const selectedRange = useMemo(
    () => (
      rangeDays === 'custom'
        ? (customStart ? buildCustomRange(customStart, customEnd) : getRangeTimes(7))
        : getRangeTimes(rangeDays)
    ),
    [rangeDays, customStart, customEnd]
  );

  const loadHeartRateRange = useCallback(async () => {
    let nextCursor = null;
    let pagesLoaded = 0;
    const maxPages = 50;
    const merged = [];

    do {
      const result = await refresh({
        ...selectedRange,
        types: ['heart_rate', 'resting_heart_rate'],
        limit: 100,
        resolution,
        cursor: nextCursor,
      });

      merged.push(...(result?.data || []));
      nextCursor = result?.pagination?.next_cursor || null;
      pagesLoaded += 1;
    } while (nextCursor && pagesLoaded < maxPages);

    setAllData(merged);
  }, [refresh, resolution, selectedRange]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadHeartRateRange();
    }
  }, [authLoading, isAuthenticated, loadHeartRateRange]);

  const heartRatePoints = useMemo(() => {
    const points = (allData || []).filter((d) =>
      d.type === 'heart_rate' || d.type === 'resting_heart_rate'
    );
    return points
      .map((p) => ({
        timestamp: new Date(p.timestamp).getTime(),
        value: p.value,
        date: new Date(p.timestamp).toLocaleDateString(),
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [allData]);

  const stats = useMemo(() => {
    if (heartRatePoints.length === 0) return null;
    const values = heartRatePoints.map((d) => d.value);
    return {
      avg: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
      min: Math.min(...values),
      max: Math.max(...values),
      count: values.length,
    };
  }, [heartRatePoints]);

  const handleRefresh = () => loadHeartRateRange();

  const handleRangeChange = (value) => {
    setRangeDays(value || '7');
  };

  const handleResolutionChange = (value) => {
    setResolution(value || 'raw');
  };

  if (authLoading) {
    return (
      <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
        <Skeleton height={400} />
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
        <Card withBorder>
          <Title order={2}>Heart Rate</Title>
          <Text c="dimmed" mt="xs">Please log in to view your heart rate data.</Text>
        </Card>
      </Container>
    );
  }

  return (
    <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
      <Stack gap="md">
        <div>
          <Title order={1} size="h2" style={{ marginTop: 0 }}>
            Heart Rate
          </Title>
        </div>

        {error && (
          <Alert color="red" variant="light">{error}</Alert>
        )}

        <Card withBorder radius="md" p="md">
          <Stack gap="md">
            <Group justify="space-between" wrap="wrap" gap="sm">
              <Group gap="sm">
                <Select
                  label="Time range"
                  data={RANGE_OPTIONS}
                  value={rangeDays}
                  onChange={handleRangeChange}
                  w={160}
                />
                {rangeDays === 'custom' && (
                  <>
                    <TextInput
                      label="Start date"
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      w={160}
                    />
                    <TextInput
                      label="End date"
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      w={160}
                    />
                    <Button
                      mt={24}
                      variant="light"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={!customStart || loading}
                    >
                      Load Data
                    </Button>
                  </>
                )}
                <Select
                  label="Resolution"
                  data={RESOLUTION_OPTIONS}
                  value={resolution}
                  onChange={handleResolutionChange}
                  w={120}
                />
              </Group>
              <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={handleRefresh} disabled={loading}>
                Refresh
              </Button>
            </Group>

            {stats && (
              <Grid>
                <Grid.Col span={{ base: 6, sm: 3 }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xl" fw={700} c="blue">{stats.avg}</Text>
                    <Text size="xs" c="dimmed">Average BPM</Text>
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: 3 }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xl" fw={700} c="green">{stats.min}</Text>
                    <Text size="xs" c="dimmed">Minimum BPM</Text>
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: 3 }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xl" fw={700} c="red">{stats.max}</Text>
                    <Text size="xs" c="dimmed">Maximum BPM</Text>
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: 3 }}>
                  <div style={{ textAlign: 'center' }}>
                  <Text size="xl" fw={700} c="purple">{stats.count}</Text>
                    <Text size="xs" c="dimmed">Data Points</Text>
                  </div>
                </Grid.Col>
              </Grid>
            )}

            <Card withBorder radius="md" p="md">
              <Group mb="md">
                <IconHeart size={20} color="var(--mantine-color-red-6)" />
                <Text fw={600}>Heart Rate Chart</Text>
              </Group>
              {loading ? (
                <Skeleton height={300} />
              ) : heartRatePoints.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={heartRatePoints}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="timestamp"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={(v) => new Date(v).toLocaleDateString()}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(v) => new Date(v).toLocaleString()}
                      formatter={(v) => [`${v} bpm`, 'Heart Rate']}
                    />
                    <Line type="monotone" dataKey="value" stroke="var(--mantine-color-red-6)" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Stack gap="sm" align="center" py="xl">
                  <Text c="dimmed" ta="center">No heart rate data available</Text>
                  <Text size="sm" c="dimmed" ta="center">
                    Make sure you have synced your wearable device data. Go to Devices to sync.
                  </Text>
                </Stack>
              )}
            </Card>

            {heartRatePoints.length > 0 && (
              <div>
                <Text fw={600} mb="xs">Recent Measurements</Text>
                <Stack gap={4}>
                  {heartRatePoints.slice(-50).reverse().map((p, i) => (
                    <Group key={i} justify="space-between" p="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                      <Text size="sm">{new Date(p.timestamp).toLocaleString()}</Text>
                      <Text size="sm" fw={500}>{p.value} bpm</Text>
                    </Group>
                  ))}
                </Stack>
              </div>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}

export default HeartRate;
